import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { GetOne_stage_Data, UpdateOne_stage_Data } from '../../Redux/Actions/Stage_Action';
import { GetAllstagetypeData } from '../../Redux/Actions/StageTypeAction';
import { Notify } from '../../Utility/Notify';
import { useSidebarWidth } from '../../Utility/useSidebarWidth';

/* ─── Icons ──────────────────────────────────────────────────────────────── */
const IconSync    = () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>;
const IconLayers  = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>;
const IconTag     = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>;
const IconNote    = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>;
const IconChevron = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>;
const IconCheck   = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>;

/* ─── Custom Select ──────────────────────────────────────────────────────── */
const CustomSelect = ({ value, onChange, options, placeholder, isDark }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useState(null);
  const ref = { current: null };

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={(el) => (ref.current = el)} style={{ position: 'relative' }}>
      <div
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', padding: '12px 16px', borderRadius: '12px', fontSize: '14px',
          border: `1.5px solid ${open ? '#2563eb' : (isDark ? '#2d3248' : '#e2e8f0')}`,
          backgroundColor: isDark ? '#151827' : '#f8fafc',
          color: value ? (isDark ? '#e5e7eb' : '#1f2937') : (isDark ? '#4b5563' : '#9ca3af'),
          fontFamily: "'DM Sans', sans-serif", cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          boxSizing: 'border-box', userSelect: 'none', transition: 'border-color 0.15s',
          boxShadow: open ? '0 0 0 3px rgba(37,99,235,0.12)' : 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ color: isDark ? '#4b5563' : '#94a3b8', display: 'flex' }}><IconTag /></span>
          <span>{selected?.label || placeholder}</span>
        </div>
        <span style={{
          display: 'flex', color: isDark ? '#6b7280' : '#94a3b8',
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s',
        }}>
          <IconChevron />
        </span>
      </div>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 9999,
          backgroundColor: isDark ? '#1e2132' : '#ffffff',
          border: `1px solid ${isDark ? '#2a2f45' : '#e2e8f0'}`,
          borderRadius: '12px', overflow: 'hidden',
          boxShadow: isDark ? '0 16px 48px rgba(0,0,0,0.5)' : '0 8px 32px rgba(0,0,0,0.12)',
        }}>
          <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
            {options.length === 0 ? (
              <div style={{ padding: '14px', fontSize: '13px', color: isDark ? '#6b7280' : '#94a3b8', textAlign: 'center' }}>
                No types available
              </div>
            ) : options.map((o) => (
              <div
                key={o.value}
                onClick={() => { onChange(o.value); setOpen(false); }}
                style={{
                  padding: '11px 16px', fontSize: '13px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  backgroundColor: o.value === value
                    ? (isDark ? 'rgba(37,99,235,0.15)' : 'rgba(37,99,235,0.07)') : 'transparent',
                  color: o.value === value
                    ? (isDark ? '#93c5fd' : '#1d4ed8') : (isDark ? '#e5e7eb' : '#1f2937'),
                  fontFamily: "'DM Sans', sans-serif", transition: 'background 0.1s',
                }}
                onMouseEnter={(e) => { if (o.value !== value) e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'; }}
                onMouseLeave={(e) => { if (o.value !== value) e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <span>{o.label}</span>
                {o.value === value && <span style={{ color: isDark ? '#93c5fd' : '#2563eb', display: 'flex' }}><IconCheck /></span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Field Label ────────────────────────────────────────────────────────── */
const FieldLabel = ({ icon, label, required, isDark }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '8px' }}>
    <span style={{ color: isDark ? '#4b5563' : '#94a3b8', display: 'flex' }}>{icon}</span>
    <span style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: isDark ? '#9ca3af' : '#6b7280' }}>
      {label}
    </span>
    {required && <span style={{ color: '#ef4444', fontSize: '14px', lineHeight: 1 }}>*</span>}
  </div>
);

/* ─── Input Style ────────────────────────────────────────────────────────── */
const inputStyle = (isDark, focused) => ({
  width: '100%', padding: '12px 16px', borderRadius: '12px', fontSize: '14px',
  border: `1.5px solid ${focused ? '#2563eb' : (isDark ? '#2d3248' : '#e2e8f0')}`,
  backgroundColor: isDark ? '#151827' : '#f8fafc',
  color: isDark ? '#e5e7eb' : '#1f2937',
  fontFamily: "'DM Sans', sans-serif",
  outline: 'none', boxSizing: 'border-box', transition: 'all 0.15s',
  boxShadow: focused ? '0 0 0 3px rgba(37,99,235,0.12)' : 'none',
});

/* ─── Main Component ─────────────────────────────────────────────────────── */
export const UpdateStages = () => {
  const { id }       = useParams();
  const dispatch     = useDispatch();
  const theme        = useTheme();
  const isDark       = theme.palette.mode === 'dark';
  const sidebarWidth = useSidebarWidth();

  const [stagename,      setStagename]      = useState('');
  const [stagetype,      setStagetype]      = useState('');
  const [stagecomment,   setStagecomment]   = useState('');
  const [loading,        setLoading]        = useState(true);
  const [updateLoading,  setUpdateLoading]  = useState(false);
  const [focusedField,   setFocusedField]   = useState(null);
  const [dataReady,      setDataReady]      = useState(false);

  useEffect(() => {
    const run = async () => {
      await dispatch(GetAllstagetypeData());
      await dispatch(GetOne_stage_Data(id));
      setDataReady(true);
    };
    run();
  }, [dispatch, id]);

  const stagestypedata       = useSelector((state) => state?.AllStagetype?.stagetype);
  const res_getstagetype_arr = Array.isArray(stagestypedata?.data) ? stagestypedata.data : [];
  const typeOptions          = res_getstagetype_arr.map((t) => ({ value: t._id, label: t.name }));

  const getonestagedata      = useSelector((state) => state?.Allstages?.stage);
  const stageData            = getonestagedata?.data || null;
  const res_update           = useSelector((state) => state.Allstages.updatestage);

  /* Populate form once data arrives */
  useEffect(() => {
    if (stageData) {
      setStagename(stageData.name || '');
      setStagetype(stageData.stagetype?._id || '');
      setStagecomment(stageData.Comment || '');
    }
  }, [stageData]);

  useEffect(() => {
    if (loading === false) {
      if (res_update?.status === 200) {
        Notify('success', 'Updated successfully', 'success');
      } else {
        Notify('error', 'Failed to update stage', 'error');
        setTimeout(() => window.location.reload(false), 1000);
      }
    }
  }, [loading]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!stagename.trim()) { Notify('error', 'Stage Name is required', 'error'); return; }
    setLoading(true);
    setUpdateLoading(true);
    await dispatch(UpdateOne_stage_Data(id, { name: stagename, stagetype, Comment: stagecomment }));
    setLoading(false);
    setUpdateLoading(false);
  };

  const previewInitials = stagename?.trim().slice(0, 2).toUpperCase() || 'ST';

  return (
    <>
      <style>{`
        @keyframes st-fade-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes st-spin     { to   { transform: rotate(360deg); } }
        .st-update-card { animation: st-fade-in 0.3s ease; }
      `}</style>

      <div style={{
        position: 'fixed', top: '50px', left: sidebarWidth, right: 0, bottom: 0,
        transition: 'left 0.25s cubic-bezier(0.4,0,0.2,1)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        backgroundColor: isDark ? '#0f1117' : '#f0f2f5',
        fontFamily: "'DM Sans','Segoe UI',sans-serif",
      }}>

        {/* ── Breadcrumb ── */}
        <div style={{
          padding: '14px 28px', flexShrink: 0,
          backgroundColor: isDark ? '#1a1d2e' : '#f0f2f5',
          display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px',
        }}>
          <Link to="/Stages" style={{ textDecoration: 'none', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 500 }}>
            Stages
          </Link>
          <span style={{ color: isDark ? '#4b5563' : '#d1d5db' }}>/</span>
          <span style={{ color: isDark ? '#e5e7eb' : '#111827', fontWeight: 600 }}>Update Stage</span>
        </div>

        {/* ── Scrollable body ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '32px 28px' }}>
          <div style={{ maxWidth: '680px' }}>

            {/* Page Title */}
            <div style={{ marginBottom: '28px' }}>
              <div style={{
                fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em',
                textTransform: 'uppercase', color: isDark ? '#4b5563' : '#94a3b8', marginBottom: '4px',
              }}>
                PIPELINE
              </div>
              <h1 style={{ fontSize: '26px', fontWeight: 800, margin: 0, color: isDark ? '#f9fafb' : '#111827', letterSpacing: '-0.5px' }}>
                Update Stage
              </h1>
            </div>

            {/* ── Main Card ── */}
            <div className="st-update-card" style={{
              backgroundColor: isDark ? '#1a1d2e' : '#ffffff',
              border: `1px solid ${isDark ? '#2a2f45' : '#e8eaf0'}`,
              borderRadius: '20px',
              boxShadow: isDark ? 'none' : '0 4px 24px rgba(0,0,0,0.07)',
              overflow: 'hidden',
            }}>

              {/* Card Header — Live Preview */}
              <div style={{
                padding: '18px 24px',
                borderBottom: `1px solid ${isDark ? '#2a2f45' : '#f1f3f8'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '42px', height: '42px', borderRadius: '12px', flexShrink: 0,
                    background: 'linear-gradient(135deg, #0D47A1, #0D47A1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '14px', fontWeight: 800, color: '#fff', transition: 'all 0.2s',
                  }}>
                    {previewInitials}
                  </div>
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: 700, color: isDark ? '#f3f4f6' : '#111827' }}>
                      {stagename || 'Stage Name'}
                    </div>
                    <div style={{ fontSize: '12px', color: isDark ? '#6b7280' : '#9ca3af', marginTop: '1px' }}>
                      {typeOptions.find((t) => t.value === stagetype)?.label || 'No type selected'}
                    </div>
                  </div>
                </div>

                {/* Editing badge */}
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '5px',
                  padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700,
                  background: isDark ? 'rgba(245,158,11,0.12)' : 'rgba(245,158,11,0.1)',
                  color: isDark ? '#fcd34d' : '#0D47A1',
                  border: `1px solid ${isDark ? 'rgba(245,158,11,0.25)' : 'rgba(245,158,11,0.2)'}`,
                }}>
                  <IconSync /> EDITING
                </span>
              </div>

              {/* Loading skeleton */}
              {!dataReady ? (
                <div style={{ padding: '32px 24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {[1, 2, 3].map((i) => (
                    <div key={i}>
                      <div style={{
                        height: '12px', width: '80px', borderRadius: '6px', marginBottom: '10px',
                        background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                        animation: 'st-pulse 1.4s ease-in-out infinite',
                      }} />
                      <div style={{
                        height: '46px', borderRadius: '12px',
                        background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
                        animation: 'st-pulse 1.4s ease-in-out infinite',
                      }} />
                    </div>
                  ))}
                  <style>{`@keyframes st-pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
                </div>
              ) : (
                /* Form Body */
                <div style={{ padding: '24px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>

                    {/* Stage Name */}
                    <div>
                      <FieldLabel icon={<IconLayers />} label="Stage Name" required isDark={isDark} />
                      <div style={{ position: 'relative' }}>
                        <span style={{
                          position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)',
                          color: isDark ? '#4b5563' : '#94a3b8', display: 'flex', pointerEvents: 'none',
                        }}>
                          <IconLayers />
                        </span>
                        <input
                          type="text"
                          placeholder="e.g. Initial Follow-up"
                          value={stagename}
                          onChange={(e) => setStagename(e.target.value)}
                          onFocus={() => setFocusedField('name')}
                          onBlur={() => setFocusedField(null)}
                          style={{ ...inputStyle(isDark, focusedField === 'name'), paddingLeft: '42px' }}
                        />
                      </div>
                    </div>

                    {/* Stage Type */}
                    <div>
                      <FieldLabel icon={<IconTag />} label="Stage Type" isDark={isDark} />
                      <CustomSelect
                        value={stagetype}
                        onChange={setStagetype}
                        options={typeOptions}
                        placeholder="Select a type…"
                        isDark={isDark}
                      />
                    </div>

                    {/* Notes */}
                    <div>
                      <FieldLabel icon={<IconNote />} label="Notes" isDark={isDark} />
                      <div style={{ position: 'relative' }}>
                        <span style={{
                          position: 'absolute', left: '14px', top: '14px',
                          color: isDark ? '#4b5563' : '#94a3b8', display: 'flex', pointerEvents: 'none',
                        }}>
                          <IconNote />
                        </span>
                        <textarea
                          placeholder="Add any notes or comments…"
                          value={stagecomment}
                          onChange={(e) => setStagecomment(e.target.value)}
                          onFocus={() => setFocusedField('notes')}
                          onBlur={() => setFocusedField(null)}
                          rows={4}
                          style={{
                            ...inputStyle(isDark, focusedField === 'notes'),
                            paddingLeft: '42px', resize: 'vertical', lineHeight: '1.6',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Card Footer — Actions */}
              <div style={{
                padding: '16px 24px',
                borderTop: `1px solid ${isDark ? '#2a2f45' : '#f1f3f8'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px',
              }}>
                <Link to="/Stages" style={{ textDecoration: 'none' }}>
                  <button
                    style={{
                      padding: '11px 24px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                      fontSize: '13px', fontWeight: 700, fontFamily: "'DM Sans',sans-serif",
                      background: isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9',
                      color: isDark ? '#d1d5db' : '#374151', transition: 'all 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.12)' : '#e2e8f0'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9'; }}
                  >
                    Cancel
                  </button>
                </Link>

                <button
                  onClick={!updateLoading ? onSubmit : undefined}
                  disabled={updateLoading || !dataReady}
                  style={{
                    padding: '11px 28px', borderRadius: '12px', border: 'none',
                    cursor: (updateLoading || !dataReady) ? 'not-allowed' : 'pointer',
                    fontSize: '13px', fontWeight: 700, fontFamily: "'DM Sans',sans-serif",
                    background: (updateLoading || !dataReady)
                      ? (isDark ? 'rgba(245,158,11,0.4)' : 'rgba(245,158,11,0.5)')
                      : 'linear-gradient(135deg, #0D47A1, #0D47A1)',
                    color: '#fff', transition: 'all 0.2s',
                    boxShadow: (updateLoading || !dataReady) ? 'none' : '0 4px 14px #0D47A1',
                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                    opacity: (updateLoading || !dataReady) ? 0.75 : 1,
                  }}
                  onMouseEnter={(e) => { if (!updateLoading && dataReady) e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  {updateLoading ? (
                    <>
                      <span style={{
                        width: '15px', height: '15px', borderRadius: '50%',
                        border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff',
                        animation: 'st-spin 0.7s linear infinite', display: 'inline-block', flexShrink: 0,
                      }} />
                      Updating…
                    </>
                  ) : (
                    <>
                      <IconSync />
                      Update Stage
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Info tip */}
            <div style={{
              marginTop: '16px', padding: '12px 16px', borderRadius: '12px', fontSize: '12px',
              background: isDark ? 'rgba(245,158,11,0.07)' : 'rgba(245,158,11,0.05)',
              border: `1px solid ${isDark ? 'rgba(245,158,11,0.18)' : 'rgba(245,158,11,0.15)'}`,
              color: isDark ? '#0D47A1' : '#0D47A1',
              display: 'flex', alignItems: 'center', gap: '8px',
            }}>
              <span style={{ fontSize: '14px' }}>✏️</span>
              <span>You are editing an existing stage. Changes will be applied immediately after saving.</span>
            </div>

          </div>
        </div>

        {/* Footer */}
        <footer style={{ padding: '14px 28px', flexShrink: 0, fontSize: '12px', color: isDark ? '#374151' : '#d1d5db' }}>
          © 2026 Realatix CRM. Smart CRM that connects customers, data, and sales in one safe place.
        </footer>
      </div>
    </>
  );
};
